import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { api } from '../api/client'
import { PageSection,
  SectionedPage,
  Card,
  EmptyState,
  ErrorBanner,
  formatDateTime,
  Spinner,
  StatCard,
  StatusBadge,
  timeAgo,
} from '../components/common'
import type { Capability, JobEvent, ProbeDetail as Detail, ProbeInterface, ProbeJobBrief, ProbeProxyStato, ProbeSighting } from '../api/types'
import { DataTable, type Column } from '../components/DataTable'
import { LogDispositivo } from '../components/LogDispositivo'
import { useAsync, usePolling } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * Scheda di una sonda: che cosa è, che cosa le è permesso fare, come sta e
 * che cosa ha fatto.
 *
 * La sonda vive in rete cliente e non è raggiungibile dal cloud: tutto ciò che
 * si vede qui arriva dai suoi heartbeat. È l'unico posto in cui si possono
 * porre domande come "su quale interfaccia vede la rete" o "con che indirizzo
 * si presenta agli apparati che interroga".
 */
export default function ProbeDetail() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  // Ricarica come l'elenco: la scheda di una sonda si guarda proprio mentre si
  // aspetta che cambi stato.
  const probe = usePolling(() => api.probe(id), 10000, [id])
  const groups = useAsync(() => api.groups(), [])
  const capabilities = useAsync(() => api.capabilities(), [])

  if (probe.loading && !probe.data) return <Spinner />
  if (probe.error && !probe.data) return <ErrorBanner error={probe.error} />
  if (!probe.data) return null

  const p = probe.data

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{p.name}</h1>
          <p className="page__subtitle">
            <StatusBadge status={p.status} />{' '}
            {p.hostname && p.hostname !== p.name && <span className="mono">{p.hostname}</span>}
            {p.group_name && <> · {t('gruppo {nome}', { nome: p.group_name })}</>}
            {p.ci_id && (
              <>
                {' · '}
                <Link to={`/cis/${p.ci_id}`}>{t('CI della macchina')}</Link>
              </>
            )}
          </p>
          <AutoinventarioStato probe={p} />
        </div>
        <Link to="/fleet">{t('← Torna alle sonde')}</Link>
      </header>

      {!p.assignable && <NotAssignable probe={p} />}
      <LocalPreference probe={p} />

      <SectionedPage
        sections={[
          { id: 'attivita', label: t('Attività') },
          { id: 'configurazione', label: t('Configurazione') },
          { id: 'flussi', label: t('Flussi') },
          { id: 'agenti', label: t('Agenti') },
          { id: 'proxy', label: t('Proxy') },
          { id: 'macchina', label: t('Macchina') },
          { id: 'discovery', label: t('Discovery') },
        ]}
      >

      <PageSection id="attivita">
      <div className="stats">
        <StatCard label={t('Job eseguiti')} value={p.activity.jobs_total} />
        <StatCard
          label={t('Negli ultimi 7 giorni')}
          value={p.activity.jobs_last_7_days}
          hint={p.activity.jobs_running ? t('{n} in corso', { n: p.activity.jobs_running }) : undefined}
        />
        <StatCard
          label={t('Riusciti')}
          value={
            p.activity.success_rate === null
              ? '—'
              : `${Math.round(p.activity.success_rate * 100)}%`
          }
          hint={
            p.activity.jobs_failed ? t('{n} falliti', { n: p.activity.jobs_failed }) : t('nessun fallimento')
          }
          tone={
            p.activity.success_rate === null
              ? undefined
              : p.activity.success_rate >= 0.9
                ? 'ok'
                : p.activity.success_rate >= 0.5
                  ? 'warn'
                  : 'error'
          }
        />
        <StatCard label={t('CI inventariati')} value={p.activity.cis_discovered} />
      </div>

      </PageSection>

      <PageSection id="configurazione">
        <ProbeForm
          probe={p}
          groups={groups.data ?? []}
          capabilities={capabilities.data ?? []}
          onSaved={() => probe.reload()}
          onDeleted={() => navigate('/fleet')}
        />
      </PageSection>

      {/* Basta che la sonda **dichiari** netflow: se la capability è solo spenta
          la scheda resta, e dice che è spenta. Nasconderla faceva sparire anche
          una cattura già configurata — che restava nelle etichette, invisibile,
          pronta a ripartire alla riaccensione senza che nessuno l'avesse
          chiesto — e rendeva l'avviso irraggiungibile proprio nel caso in cui
          serve. Se il binario non dichiara netflow non c'è nulla da
          configurare, e allora sparisce davvero. */}
      {p.capabilities.includes('netflow') && (
        <PageSection id="flussi">
          <FlowSources probe={p} onSaved={() => probe.reload()} />
        </PageSection>
      )}

      {/* L'ascolto passivo: la sonda lo dichiara come capability; il riquadro
          dice se sta ascoltando, su cosa, e — soprattutto — chi ha sentito.
          È la risposta a «funziona?»: una tabella di dispositivi. */}
      {p.capabilities.includes('passive_listen') && (
        <PageSection id="ascolto">
          <AscoltoPassivo probe={p} />
        </PageSection>
      )}

      {/* Fuori dalla condizione su netflow: le consegne degli agenti non
          c'entrano con i flussi, e legarle a quella capability avrebbe reso la
          funzione invisibile su una sonda compilata senza — cioè proprio dove
          può servire. */}
      <PageSection id="agenti">
        <InventoryRelay probe={p} onSaved={() => probe.reload()} />
      </PageSection>

      <PageSection id="proxy">
        <ProxySonda probe={p} onSaved={() => probe.reload()} />
      </PageSection>

      <PageSection id="macchina">
      <Card title={t('Macchina')}>
        <HostFacts probe={p} />
      </Card>

      <Card title={t('Manutenzione della sonda')}>
        <Manutenzione
          version={p.version}
          servizio={String((p.metrics ?? {}).service_name ?? 'censo-agent')}
          comandoAggiornatore={p.updater_command}
          appliance={Boolean(p.censo_appliance)}
        />
      </Card>

      <Card title={t('Log della sonda')}>
        <p className="field__hint" style={{ marginTop: 0 }}>
          {t('Su richiesta: la sonda riceve la richiesta con il prossimo heartbeat e consegna la coda del suo registro (il journal dell’unità quando può leggerlo, altrimenti le ultime righe tenute in memoria dal processo). Si legge qui, si filtra e si scarica.')}
        </p>
        <LogDispositivo kind="probe" id={p.id} nome={p.name} />
      </Card>

      <Card title={t('Interfacce di rete ({n})', { n: p.interfaces.length })}>
        <Interfaces interfaces={p.interfaces} />
      </Card>

      </PageSection>

      <PageSection id="discovery">
      <Card title={t('Ultime discovery')}>
        {!p.recent_jobs.length ? (
          <EmptyState
            title={t('Nessun job eseguito')}
            hint={t('Questa sonda non ha ancora ricevuto lavoro: verifica che sia online e che abbia una capability abilitata.')}
          />
        ) : (
          <DataTable rows={p.recent_jobs} columns={colonneJob()} rowKey={(job) => job.id} conteggio={{ nome: t('job') }} />
        )}
      </Card>
      </PageSection>
      </SectionedPage>
    </>
  )
}

/**
 * Spiega perché una sonda non riceve job.
 *
 * È la domanda che si pone chi apre questa pagina quando una discovery resta in
 * attesa, e senza una risposta esplicita si finisce a cercare il guasto nella
 * rete del cliente invece che in una casella disattivata.
 */
function NotAssignable({ probe }: { probe: Detail }) {
  const reasons: string[] = []
  if (!probe.enabled) reasons.push(t('è disattivata dalla console'))
  if (probe.status !== 'online')
    reasons.push(
      probe.degraded_reason
        ? t('non è online (stato: {stato}, {motivo})', { stato: probe.status, motivo: probe.degraded_reason })
        : t('non è online (stato: {stato})', { stato: probe.status }),
    )
  if (probe.effective_capabilities.length === 0)
    reasons.push(
      probe.capabilities.length === 0
        ? t('non ha dichiarato alcuna capability')
        : t('non ha capability abilitate'),
    )

  return (
    <div className="banner banner--warn">
      <strong>{t('Questa sonda non riceve job:')}</strong> {reasons.join('; ')}.
      {probe.unavailable_capabilities.length > 0 && (
        <>
          {' '}
          {t(
            'Le capability {elenco} sono abilitate ma il binario della sonda non le implementa: aggiorna la sonda o avviala senza CENSO_CAPABILITIES che le esclude.',
            { elenco: probe.unavailable_capabilities.join(', ') },
          )}
        </>
      )}
    </div>
  )
}

/**
 * Avverte quando la sonda chiede localmente qualcosa di diverso da ciò che è
 * abilitato qui.
 *
 * `CENSO_CAPABILITIES` imposta solo la politica con cui la sonda si arruola:
 * dopo decide il Control Plane. Ma chi ha scritto quella variabile si aspetta
 * che valga, e scoprire il contrario da un job che parte quando non doveva — o
 * che non parte quando doveva — è il modo peggiore.
 */
function LocalPreference({ probe }: { probe: Detail }) {
  const local = probe.local_preference
  if (!local) return null

  const effective = probe.effective_capabilities
  const identical =
    local.length === effective.length && local.every((c) => effective.includes(c))
  if (identical) return null

  return (
    <div className="banner banner--info">
      {t('La sonda è avviata con {variabile}, ma le capability effettive sono {effettive}. Vale quest’ultima: la variabile imposta solo la politica iniziale all’arruolamento, così una scelta fatta da qui non viene riscritta dal prossimo heartbeat. Puoi rimuoverla dalla riga di comando della sonda senza cambiare nulla.', {
        variabile: `CENSO_CAPABILITIES=${local.join(',')}`,
        effettive: effective.join(', ') || t('nessuna'),
      })}
    </div>
  )
}

/** Modifica di ciò che appartiene all'operatore. */
function ProbeForm({
  probe,
  groups,
  capabilities,
  onSaved,
  onDeleted,
}: {
  probe: Detail
  groups: { id: string; name: string }[]
  capabilities: Capability[]
  onSaved: () => void
  onDeleted: () => void
}) {
  const [name, setName] = useState(probe.name)
  const [groupId, setGroupId] = useState(probe.group_id ?? '')
  const [role, setRole] = useState(probe.role)
  const [enabled, setEnabled] = useState(probe.enabled)
  const [autoUpdate, setAutoUpdate] = useState(Boolean(probe.auto_update))
  const [restrict, setRestrict] = useState(probe.enabled_capabilities !== null)
  const [selected, setSelected] = useState<string[]>(
    probe.enabled_capabilities ?? probe.capabilities,
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [saved, setSaved] = useState(false)

  // La pagina si ricarica da sola ogni dieci secondi: senza risincronizzare il
  // modulo, i campi resterebbero fermi sui valori con cui la pagina è stata
  // aperta e un salvataggio successivo riscriverebbe di nascosto ciò che
  // qualcun altro ha nel frattempo cambiato.
  useEffect(() => {
    setName(probe.name)
    setGroupId(probe.group_id ?? '')
    setRole(probe.role)
    setEnabled(probe.enabled)
    setAutoUpdate(Boolean(probe.auto_update))
    setRestrict(probe.enabled_capabilities !== null)
    setSelected(probe.enabled_capabilities ?? probe.capabilities)
    // Solo quando cambia ciò che il server dice, non a ogni battuta sulla
    // tastiera: dipendere dagli stati locali azzererebbe il modulo mentre lo
    // si compila.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    probe.name,
    probe.group_id,
    probe.role,
    probe.enabled,
    probe.auto_update,
    JSON.stringify(probe.enabled_capabilities),
  ])

  // Le capability che la sonda dichiara ma che non sono a catalogo (una sonda
  // più recente del Control Plane) devono restare governabili: nasconderle
  // significherebbe non poterle disabilitare.
  const options = [
    ...capabilities,
    ...probe.capabilities
      .filter((c) => !capabilities.some((known) => known.capability === c))
      .map((c) => ({
        capability: c,
        label: c,
        hint: t('Dichiarata dalla sonda ma non a catalogo: la sonda è più recente del Control Plane.'),
        feature: c,
        available: true,
        // Non a catalogo: non si sa mappare a una licenza, quindi non si blocca
        // — e comunque è già attiva sulla sonda, va lasciata disabilitabile.
        licensed: true,
        license_reason: null as string | null,
      })),
  ]

  function toggle(capability: string) {
    setSelected((current) =>
      current.includes(capability)
        ? current.filter((c) => c !== capability)
        : [...current, capability],
    )
  }

  async function save() {
    setBusy(true)
    setError(null)
    setSaved(false)
    try {
      await api.updateProbe(probe.id, {
        name: name.trim(),
        group_id: groupId || null,
        role,
        enabled,
        auto_update: autoUpdate,
        capabilities: restrict ? selected : null,
      })
      setSaved(true)
      onSaved()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (
      !window.confirm(
        t('Rimuovere la sonda «{nome}»? I CI che ha inventariato restano; le discovery in corso vanno concluse prima.', { nome: probe.name }),
      )
    )
      return
    setBusy(true)
    setError(null)
    try {
      await api.deleteProbe(probe.id)
      onDeleted()
    } catch (err) {
      setError(err)
      setBusy(false)
    }
  }

  return (
    <Card title={t('Configurazione')}>
      <ErrorBanner error={error} />
      {saved && <div className="banner banner--ok">{t('Modifiche salvate.')}</div>}

      <div className="row">
        <div className="field">
          <label htmlFor="p-name">{t('Nome')}</label>
          <input id="p-name" type="text" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="p-group">{t('Gruppo')}</label>
          <select id="p-group" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            <option value="">{t('Nessun gruppo')}</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="p-role">{t('Ruolo nel gruppo')}</label>
          <select id="p-role" value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="active">{t('Attiva')}</option>
            <option value="standby">{t('Riserva')}</option>
          </select>
          <p className="field__hint">
            {t('Conta nei gruppi in failover: le riserve ricevono lavoro solo se le attive mancano.')}
          </p>
        </div>
      </div>

      <div className="checkbox">
        <input
          id="p-auto-update"
          type="checkbox"
          checked={autoUpdate}
          onChange={(e) => setAutoUpdate(e.target.checked)}
        />
        <label htmlFor="p-auto-update">
          {t('Aggiornamento automatico')}
          <small>
            {t('Quando il Control Plane passa a una versione nuova, la sonda si aggiorna da sola in un momento a caso (entro 3 minuti fino a cinque sonde, entro 10 fino a venti, entro 20 oltre), senza premere «Aggiorna»: così una fleet non si riavvia tutta insieme. Se è l’unica sonda del suo gruppo pesca nella seconda metà della finestra. Il freno resta: se gli aggiornamenti falliscono, le sonde non ancora partite si fermano, e non c’è mai un nuovo tentativo automatico su una sonda che ha già rifiutato quella versione. La sonda si riavvia: per qualche istante la sua rete resta senza raccolta.')}
          </small>
        </label>
      </div>

      <div className="checkbox">
        <input
          id="p-enabled"
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        <label htmlFor="p-enabled">
          {t('Sonda attiva')}
          <small>
            {t('Disattivandola smette di ricevere job ma resta arruolata e continua a mandare heartbeat: serve a metterla da parte durante una manutenzione senza doverla rimuovere e riarruolare.')}
          </small>
        </label>
      </div>

      <div className="checkbox">
        <input
          id="p-restrict"
          type="checkbox"
          checked={restrict}
          onChange={(e) => setRestrict(e.target.checked)}
        />
        <label htmlFor="p-restrict">
          {t('Limita le capability')}
          <small>
            {t('Senza limiti la sonda esegue tutto ciò che dichiara ({elenco}).', { elenco: probe.capabilities.join(', ') || t('nulla') })}
          </small>
        </label>
      </div>

      {restrict && (
        <fieldset style={{ border: 0, padding: 0, margin: '0 0 16px' }}>
          {options.map((c) => {
            const declared = probe.capabilities.includes(c.capability)
            // Un modulo non coperto dalla licenza non si può attivare: si
            // mostra bloccato. Se fosse già selezionato (deroga tolta dopo
            // l'attivazione) lo si lascia togliere, non aggiungere.
            const locked = c.licensed === false && !selected.includes(c.capability)
            return (
              <div className="checkbox" key={c.capability}>
                <input
                  id={`edit-cap-${c.capability}`}
                  type="checkbox"
                  checked={selected.includes(c.capability)}
                  disabled={locked}
                  onChange={() => toggle(c.capability)}
                />
                <label htmlFor={`edit-cap-${c.capability}`}>
                  {t(c.label)} <span className="mono">({c.capability})</span>
                  <small>
                    {c.licensed === false ? (
                      <span style={{ color: 'var(--warn)' }}>
                        {t('Non compreso nella licenza')}
                      </span>
                    ) : declared ? (
                      t(c.hint)
                    ) : c.available === false ? (
                      // Non è un problema di questa sonda: nessuna versione la
                      // implementa. Dire "aggiorna la sonda" sarebbe un
                      // consiglio che non porta da nessuna parte.
                      <span className="text-muted">{t(c.hint)}</span>
                    ) : (
                      // Con una sonda aggiornata questo caso non si presenta:
                      // dichiara tutti i connettori che ha. Resta per le sonde
                      // più vecchie del Control Plane, dove abilitare qualcosa
                      // che il binario non ha non lo aggiunge — e dirlo prima
                      // evita di cercare il guasto altrove.
                      <span style={{ color: 'var(--warn)' }}>
                        {t('Non dichiarata da questa sonda: il suo binario non la implementa. Aggiornala per renderla disponibile.')}
                      </span>
                    )}
                  </small>
                </label>
              </div>
            )
          })}
          {selected.length === 0 && (
            <div className="banner banner--warn">
              {t('Nessuna capability abilitata: la sonda resterà ferma.')}
            </div>
          )}
        </fieldset>
      )}

      <div className="toolbar">
        <button onClick={save} disabled={busy}>
          {busy ? t('Salvataggio…') : t('Salva modifiche')}
        </button>
        <button className="danger" onClick={remove} disabled={busy}>
          {t('Rimuovi sonda')}
        </button>
      </div>
    </Card>
  )
}

/**
 * Da dove la sonda prende i flussi.
 *
 * Due sorgenti indipendenti: i flussi che gli **apparati esportano** verso la
 * sonda, e il traffico che la sonda **osserva direttamente** da una porta
 * mirror. La seconda serve dove NetFlow non c'è — switch che non lo
 * implementano, apparati che non si possono riconfigurare.
 *
 * L'interfaccia si sceglie fra quelle che la sonda **dichiara davvero** nella
 * sua telemetria: un campo di testo libero accetterebbe un nome che non esiste,
 * e il risultato sarebbe una cattura che non parte con l'errore in un log
 * dentro la rete del cliente.
 */
/**
 * Il ricevitore delle consegne degli agenti d'inventario.
 *
 * ## Perché sta qui e non in una variabile del container
 *
 * Era l'unica impostazione della sonda che si cambiava a mano: una variabile
 * d'ambiente, un redeploy, su una macchina che sta in casa del cliente. Tutto
 * il resto — capability, porta dei flussi, cattura diretta — si decide da qui.
 *
 * Costava anche più di quanto sembri: chi configura un agente vede l'elenco
 * delle sonde a cui consegnare, e per farne comparire una doveva raggiungere
 * un'altra persona con accesso al sito. Due mani per un'operazione sola.
 *
 * ## Perché nasce spento
 *
 * È l'unica porta che una sonda apre in ingresso. Serve dove esistono macchine
 * che né la sonda raggiunge né raggiungono il Control Plane — una DMZ senza
 * uscita — e nella maggior parte delle installazioni non serve affatto.
 */
/**
 * L'inventario che la sonda fa della macchina su cui gira: quando, con che
 * esito, o perché no (in un container descriverebbe il container).
 */
function AutoinventarioStato({ probe }: { probe: Detail }) {
  const stato = (probe.metrics ?? {}).self_inventory as
    | { last_at?: string; error?: string; skipped?: string }
    | undefined
  if (!stato) return null
  if (stato.skipped === 'container') {
    return (
      <p className="field__hint">
        {t('Autoinventario non eseguito: la sonda gira in un container, e l’inventario descriverebbe il container invece della macchina.')}
      </p>
    )
  }
  if (stato.error) {
    return <p className="banner banner--warn">{t('Autoinventario non consegnato: {errore}', { errore: stato.error })}</p>
  }
  return stato.last_at ? (
    <p className="field__hint">{t('Autoinventario della macchina: {quando}', { quando: formatDateTime(stato.last_at) })}</p>
  ) : null
}

/** Le esclusioni proposte: la rete locale esce diretta. */
const NO_PROXY_PREDEFINITO = ['localhost', '127.0.0.0/8', '10.0.0.0/8', '172.16.0.0/12', '192.168.0.0/16']

/**
 * Il proxy HTTP della sonda.
 *
 * Due usi distinti: il canale verso il Control Plane (scelto qui, fra «come
 * all'installazione», «diretto» e «tramite il proxy») e i connettori, che
 * nella propria scheda possono scegliere «proxy della sonda». La sonda applica
 * un cambio del canale **solo dopo averlo provato**: se il Control Plane non
 * risponde così, tiene quello che funziona e lo dice qui sotto.
 */
function ProxySonda({ probe, onSaved }: { probe: Detail; onSaved: () => void }) {
  const attuale = probe.proxy ?? { has_password: false }
  const [url, setUrl] = useState(attuale.url ?? '')
  const [utente, setUtente] = useState(attuale.username ?? '')
  const [password, setPassword] = useState('')
  const [togliPassword, setTogliPassword] = useState(false)
  const [esclusioni, setEsclusioni] = useState(
    (attuale.no_proxy ?? (attuale.url ? [] : NO_PROXY_PREDEFINITO)).join('\n'),
  )
  const [canale, setCanale] = useState(attuale.control_plane ?? 'install')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const stato = (probe.metrics ?? {}).proxy as ProbeProxyStato | undefined

  async function save() {
    setBusy(true)
    setError(null)
    try {
      await api.updateProbe(probe.id, {
        proxy: {
          url: url.trim(),
          username: utente.trim(),
          ...(password ? { password } : togliPassword ? { password: '' } : {}),
          no_proxy: esclusioni.split(/[\n,]/).map((x) => x.trim()).filter(Boolean),
          control_plane: canale,
        },
      })
      setPassword('')
      setTogliPassword(false)
      onSaved()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  const nomeCanale = (m?: string) =>
    m === 'direct' ? t('diretto') : m === 'proxy' ? t('tramite il proxy') : t("come all'installazione")

  return (
    <Card title={t('Proxy')}>
      <ErrorBanner error={error} />
      <p className="muted" style={{ marginTop: 0 }}>
        {t('Il proxy HTTP che questa sonda usa per uscire. Serve per la comunicazione con il Control Plane, se lo scegli qui sotto, e per i connettori che nella loro scheda scelgono «proxy della sonda».')}
      </p>
      <div className="field">
        <label htmlFor="proxy-url">{t('Indirizzo del proxy')}</label>
        <input
          id="proxy-url"
          placeholder="http://proxy.azienda.local:3128"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <p className="field__hint">{t('http, https o socks5. Vuoto = la sonda non ha un proxy.')}</p>
      </div>
      <div className="row">
        <div className="field">
          <label htmlFor="proxy-user">{t('Utente (facoltativo)')}</label>
          <input id="proxy-user" value={utente} onChange={(e) => setUtente(e.target.value)} autoComplete="off" />
        </div>
        <div className="field">
          <label htmlFor="proxy-pass">{t('Password')}</label>
          <input
            id="proxy-pass"
            type="password"
            value={password}
            autoComplete="new-password"
            placeholder={attuale.has_password ? t('salvata — lascia vuoto per non cambiarla') : ''}
            onChange={(e) => setPassword(e.target.value)}
          />
          {attuale.has_password && (
            <label className="check">
              <input type="checkbox" checked={togliPassword} onChange={(e) => setTogliPassword(e.target.checked)} />{' '}
              {t('Togli la password salvata')}
            </label>
          )}
        </div>
      </div>
      <div className="field">
        <label htmlFor="proxy-no">{t('Destinazioni che escono dirette')}</label>
        <textarea
          id="proxy-no"
          rows={4}
          value={esclusioni}
          onChange={(e) => setEsclusioni(e.target.value)}
        />
        <p className="field__hint">
          {t('Una per riga: host, domini (.azienda.local), IP o reti (10.0.0.0/8). Proposte le reti private: vCenter, iDRAC e storage in LAN non passano dal proxy.')}
        </p>
      </div>
      <div className="field">
        <label htmlFor="proxy-cp">{t('Comunicazione con il Control Plane')}</label>
        <select id="proxy-cp" value={canale} onChange={(e) => setCanale(e.target.value as typeof canale)}>
          <option value="install">{t("Come all'installazione (CENSO_PROXY, altrimenti diretta)")}</option>
          <option value="direct">{t('Diretta, senza proxy')}</option>
          <option value="proxy">{t('Tramite questo proxy')}</option>
        </select>
        <p className="field__hint">
          {t('La sonda prova il nuovo modo prima di adottarlo: se il Control Plane non risponde così, resta sul modo che funziona e l’errore compare qui. Un proxy che ispeziona il TLS rompe l’identità mTLS della sonda: va escluso dall’ispezione.')}
        </p>
      </div>

      {/* Chiesto e fatto sono due cose diverse: la sonda applica al giro
          successivo, e solo se la prova riesce. */}
      {stato && (
        <p className={stato.error ? 'banner banner--warn' : 'muted'}>
          {t('Canale attuale: {come}', { come: nomeCanale(stato.control_plane) })}
          {stato.url ? ` (${stato.url})` : ''}
          {stato.install_proxy ? ` · ${t('proxy di installazione: {p}', { p: stato.install_proxy })}` : ''}
          {stato.desired && !stato.error ? ` · ${t('in applicazione: {come}', { come: nomeCanale(stato.desired) })}` : ''}
          {stato.error ? <><br />{stato.error}</> : null}
        </p>
      )}

      <div className="rowactions" style={{ textAlign: 'left' }}>
        <button onClick={save} disabled={busy}>
          {busy ? t('Salvataggio…') : t('Salva')}
        </button>
      </div>
    </Card>
  )
}

function InventoryRelay({ probe, onSaved }: { probe: Detail; onSaved: () => void }) {
  const labels = probe.labels ?? {}
  const [acceso, setAcceso] = useState(labels.inventory_relay === 'true')
  const [porta, setPorta] = useState(labels.inventory_relay_port ?? '8899')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  // Ciò che la sonda dichiara di **fare davvero**, che non è ciò che le
  // abbiamo chiesto: fra la spunta e la porta aperta passa un giro di
  // configurazione, e senza questa riga i due stati sarebbero indistinguibili.
  const attivo = (probe.metrics ?? {}).inventory_relay as
    | { port?: string; addresses?: string[] }
    | undefined

  async function save() {
    setBusy(true)
    setError(null)
    try {
      await api.updateProbe(probe.id, {
        labels: {
          ...labels,
          inventory_relay: acceso ? 'true' : 'false',
          inventory_relay_port: porta,
        },
      })
      onSaved()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={t("Consegne degli agenti d'inventario")}>
      <ErrorBanner error={error} />
      <p className="muted" style={{ marginTop: 0 }}>
        {t('Le macchine che nessuna sonda raggiunge raccolgono il proprio inventario e lo consegnano da sé. Quelle che nemmeno raggiungono il Control Plane — una DMZ senza uscita — possono consegnarlo a questa sonda, che lo inoltra senza aprirlo: il carico è sigillato.')}
      </p>
      <label className="check">
        <input
          type="checkbox"
          checked={acceso}
          onChange={(e) => setAcceso(e.target.checked)}
        />{' '}
        {t('Ricevi le consegne su questa sonda')}
      </label>
      <div className="field" style={{ maxWidth: 200 }}>
        <label htmlFor="relay-port">{t('Porta')}</label>
        <input
          id="relay-port"
          type="number"
          min={1}
          max={65535}
          value={porta}
          disabled={!acceso}
          onChange={(e) => setPorta(e.target.value)}
        />
        <p className="field__hint">
          {t("Deve essere raggiungibile dalle macchine che consegnano, e da nessun altro: è l'unica porta che una sonda apre in ingresso.")}
        </p>
      </div>

      {/* Chiesto e fatto sono due cose diverse, e vanno mostrate separate: fra
          la spunta e la porta aperta passa un giro di configurazione, e se
          qualcosa lo impedisce nessun'altra schermata lo direbbe. */}
      <p className="muted">
        {attivo?.port
          ? attivo.addresses?.length
            ? t('In ascolto sulla porta {port} ({indirizzi}).', { port: attivo.port, indirizzi: attivo.addresses.join(', ') })
            : t('In ascolto sulla porta {port}.', { port: attivo.port })
          : acceso
            ? t('Non ancora in ascolto: la sonda applica la scelta al prossimo giro di configurazione, entro un minuto.')
            : t('Non in ascolto.')}
      </p>

      <div className="rowactions" style={{ textAlign: 'left' }}>
        <button onClick={save} disabled={busy}>
          {busy ? t('Salvataggio…') : t('Salva')}
        </button>
      </div>
    </Card>
  )
}

// The MTUs the probe accepts (capture.MaxMTU, flow_config_service).
const MTU_MIN = 576
const MTU_MAX = 9600
const MTU_PRESETS = ['', '1500', '9000', '9216']

function FlowSources({ probe, onSaved }: { probe: Detail; onSaved: () => void }) {
  const labels = probe.labels ?? {}
  // Ciò che la sonda **dice** di stare facendo: porta effettiva, ascolto attivo,
  // e l'eventuale motivo per cui la porta non si è aperta. È la conferma che il
  // cambio di porta è andato in porto (o la diagnosi se non è così).
  const fl = probe.flows
  const [iface, setIface] = useState(labels.flow_capture ?? '')
  // Il default è 1 su 10, lo stesso della sonda: mostrare qui «tutti i pacchetti»
  // per una cattura che ne processa uno su dieci farebbe credere misurato ciò
  // che è stimato, e il primo salvataggio cambierebbe la raccolta senza che
  // nessuno l'abbia chiesto.
  const [sampling, setSampling] = useState(labels.flow_capture_sampling ?? '10')
  const [listen, setListen] = useState(labels.flow_listen !== 'false')
  // The capture interface itself, set up by the probe before it listens. Empty
  // MTU = leave it as it is: changing the MTU of an interface is not something
  // to do because a form was saved for another reason.
  const savedMtu = labels.flow_capture_mtu && labels.flow_capture_mtu !== '0' ? labels.flow_capture_mtu : ''
  const [mtu, setMtu] = useState(savedMtu)
  const [mtuOther, setMtuOther] = useState(!MTU_PRESETS.includes(savedMtu))
  const [offloadsOff, setOffloadsOff] = useState(labels.flow_capture_offloads_off === 'true')
  // La porta su cui gli apparati esportano. 2055 è la più diffusa, ma non è
  // universale — Cisco esporta anche su 9995/9996, IPFIX usa 4739, sFlow 6343 —
  // e su una sonda che sta in una rete altrui la porta la decide chi ha
  // configurato gli apparati, non noi.
  const [port, setPort] = useState(labels.flow_port ?? '2055')
  // Sotto queste soglie una conversazione non vale la pena di essere inviata: un
  // ping è quattro pacchetti e non dice niente su cosa espone un servizio.
  //
  // Stanno sulla **sonda** e non fra le impostazioni del tenant perché dipendono
  // dal punto di osservazione: la stessa soglia su una dorsale e su una rete
  // d'ufficio significa due cose diverse.
  // Forwarding: a copy of every received packet to other collectors (a
  // VMware vDS exports to one collector only). One destination per line here,
  // commas in the label.
  const [forward, setForward] = useState((labels.flow_forward ?? '').split(',').map((x) => x.trim()).filter(Boolean).join('\n'))
  const [keepSource, setKeepSource] = useState(labels.flow_forward_keep_source === 'true')
  const [minPackets, setMinPackets] = useState(labels.flow_min_packets ?? '0')
  const [minBytes, setMinBytes] = useState(labels.flow_min_bytes ?? '0')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  // Le interfacce spente o di loopback non servono a catturare: una porta
  // mirror è per definizione attiva, e proporle allungherebbe l'elenco con
  // scelte che non possono funzionare.
  const usable = probe.interfaces.filter((i) => i.up && !i.loopback && i.name)

  // Validata qui e non solo sul server, perché il server **non rifiuta**: una
  // porta fuori intervallo viene sostituita in silenzio con 2055 (è la regola
  // giusta per un'etichetta scritta a mano, che non deve poter fermare la
  // raccolta). Senza questo controllo, chi scrive 70000 vedrebbe «salvato» e
  // riceverebbe i flussi su un'altra porta senza che nulla lo dica.
  const portNumber = Number(port)
  const portError =
    !port.trim() || !Number.isInteger(portNumber) || portNumber < 1 || portNumber > 65535
      ? t('Deve essere un numero fra 1 e 65535.')
      : portNumber < 1024
        ? t('Sotto 1024 servono privilegi che la sonda non ha: scegli una porta più alta.')
        : null

  // The server refuses it too: here so that the reason shows before saving.
  const mtuNumber = Number(mtu)
  const mtuError =
    mtu.trim() && (!Number.isInteger(mtuNumber) || mtuNumber < MTU_MIN || mtuNumber > MTU_MAX)
      ? t('Deve essere un numero fra {min} e {max}, o vuoto per non toccarla.', { min: MTU_MIN, max: MTU_MAX })
      : null

  // L'interfaccia **già configurata** resta comunque fra le opzioni, anche se il
  // filtro la escluderebbe o se la sonda non la dichiara più. Senza, il campo
  // mostrerebbe «Nessuna» pur essendo la cattura attiva, e il primo salvataggio
  // fatto per tutt'altro motivo la spegnerebbe senza che nessuno l'abbia chiesto.
  const options = usable.map((i) => ({
    name: i.name as string,
    detail: i.addresses.length ? i.addresses[0] : t('senza indirizzo'),
  }))
  if (iface && !options.some((o) => o.name === iface)) {
    options.unshift({ name: iface, detail: t('configurata') })
  }

  async function save() {
    setBusy(true)
    setError(null)
    try {
      await api.updateProbe(probe.id, {
        labels: {
          ...labels,
          flow_capture: iface,
          flow_capture_sampling: sampling,
          flow_capture_mtu: mtu.trim(),
          flow_capture_offloads_off: offloadsOff ? 'true' : 'false',
          flow_listen: listen ? 'true' : 'false',
          flow_port: port,
          flow_forward: forward.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean).join(','),
          flow_forward_keep_source: keepSource ? 'true' : 'false',
          flow_min_packets: minPackets,
          flow_min_bytes: minBytes,
        },
      })
      onSaved()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={t('Sorgenti dei flussi')}>
      <ErrorBanner error={error} />

      {/* Due sorgenti indipendenti, tenute separate: la sonda può ricevere i
          flussi che gli apparati **esportano** (collector UDP) e/o osservare il
          traffico **dal filo** (porta mirror). Sono cose diverse e si
          configurano a parte. */}

      {/* --- Sorgente 1: collector (ricezione flussi esportati) --- */}
      <h4 style={{ margin: '4px 0 8px', fontSize: 14 }}>
        {t('Ricezione dei flussi esportati (collector)')}
      </h4>
      <div className="checkbox">
        <input
          id="fl-listen"
          type="checkbox"
          checked={listen || !iface}
          disabled={!iface}
          onChange={(e) => setListen(e.target.checked)}
        />
        <label htmlFor="fl-listen">
          {t('Ricevi i flussi esportati dagli apparati')}
          <small>
            {t('Mette la sonda in ascolto sulla porta UDP indicata qui sotto. Si può chiudere solo se la cattura diretta è attiva: senza nessuna delle due, la sonda sembrerebbe raccogliere senza raccogliere nulla.')}
          </small>
        </label>
      </div>
      <div className="row">
        {/* La porta è un dato dell'impianto del cliente, non una preferenza
            nostra: gli apparati esportano dove sono già configurati, e chiedere
            di riconfigurare uno switch per far contento un collector è il modo
            più sicuro di non essere installabili. Il valore vale per **questa**
            sonda: due siti dello stesso cliente possono usare porte diverse. */}
        <div className="field" style={{ maxWidth: 230 }}>
          <label htmlFor="fl-port">{t('Porta di ascolto (UDP)')}</label>
          <input
            id="fl-port"
            type="number"
            min={1}
            max={65535}
            value={port}
            disabled={!listen && !!iface}
            onChange={(e) => setPort(e.target.value)}
          />
          <p className="field__hint">
            {portError ? (
              <strong>{portError}</strong>
            ) : (
              <>
                {t('2055 è la più diffusa. NetFlow v9 di Cisco usa spesso 9995 o 9996, IPFIX 4739, sFlow 6343. Deve corrispondere a dove gli apparati esportano già.')}
              </>
            )}
          </p>
          {/* Stato reale riportato dalla sonda: conferma che la porta nuova è
              stata aperta, o dice perché no. */}
          {fl && fl.enabled && (
            fl.listen_error ? (
              <p className="field__hint" style={{ color: 'var(--warn)' }}>
                <strong>{t('Non in ascolto: {err}', { err: fl.listen_error })}</strong>
              </p>
            ) : fl.listening && fl.port ? (
              <p className="field__hint" style={{ color: 'var(--ok, #2e7d32)' }}>
                {t('In ascolto sulla porta {port}.', { port: fl.port })}{' '}
                {(fl.received_packets ?? 0) > 0 ? (
                  <span className="text-muted">
                    {t('{n} pacchetti ricevuti.', { n: fl.received_packets })}
                    {typeof fl.decoded_flows === 'number' && (
                      <> {t('{n} flussi decodificati.', { n: fl.decoded_flows })}</>
                    )}
                  </span>
                ) : (
                  <span className="text-muted">{t('Nessun flusso ricevuto ancora: verifica che un apparato esporti su questa porta.')}</span>
                )}
                {/* Il segnale che prima si scopriva solo scavando nei log: i
                    pacchetti arrivano ma nessuno si decodifica. Solo quando la
                    sonda riporta davvero 0 (non quando non lo riporta affatto). */}
                {(fl.received_packets ?? 0) > 0 && fl.decoded_flows === 0 && (
                  <span style={{ color: 'var(--warn)', display: 'block', marginTop: 4 }}>
                    {t('Arrivano pacchetti ma nessun flusso viene decodificato: probabile un template non ancora ricevuto o un formato che la sonda non interpreta.')}
                  </span>
                )}
              </p>
            ) : fl.config_seen ? (
              <p className="field__hint">
                {t('Non ancora in ascolto: la sonda applica la scelta al prossimo giro di configurazione, entro un minuto.')}
              </p>
            ) : null
          )}
        </div>
      </div>

      {/* Diagnosi flussi: la sonda annusa la porta configurata e dice se e a
          chi arrivano i flussi. È la risposta a «sembra che arrivi NetFlow ma
          non lo rileva» senza shell né Wireshark. */}
      <DiagnosiFlussi probe={probe} />

      {/* Forwarding: the probe as a replicator, for exporters that send to
          one collector only (VMware vDS). */}
      <h4 style={{ margin: '18px 0 8px', fontSize: 14 }}>{t('Inoltro ad altri collector')}</h4>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Alcuni apparati esportano i flussi verso un solo collector, come il distributed switch di VMware: con la sonda lì, gli altri strumenti (NMS, SIEM, ntopng) li perderebbero. La sonda manda a ogni destinazione una copia di ogni pacchetto che riceve, intatta e prima di qualunque campionamento, template compresi.')}
      </p>
      <div className="field">
        <label htmlFor="fl-forward">{t('Destinazioni (indirizzo:porta, una per riga, al più 8)')}</label>
        <textarea id="fl-forward" rows={3} value={forward} disabled={!listen && !!iface}
                  placeholder={'10.0.0.20:2055\nnms.ospedale.local:9995'}
                  onChange={(e) => setForward(e.target.value)} />
      </div>
      <div className="checkbox">
        <input id="fl-forward-src" type="checkbox" checked={keepSource} disabled={!listen && !!iface}
               onChange={(e) => setKeepSource(e.target.checked)} />
        <label htmlFor="fl-forward-src">
          {t('Mantieni l’indirizzo dell’esportatore come mittente')}
          <small>
            {t('Il collector di destinazione vede il vDS come se esportasse a lui, non la sonda: serve quando distingue gli apparati dall’indirizzo o ne riceve più d’uno. Richiede il permesso della cattura (CAP_NET_RAW) e solo IPv4; un router con il controllo anti-spoofing (uRPF) fra sonda e destinazione scarta questi pacchetti. Senza la spunta i pacchetti partono dalla sonda: il collector va configurato per accettarli dal suo indirizzo.')}
          </small>
        </label>
      </div>
      {fl?.forward && fl.forward.length > 0 && (
        <ul className="field__hint" style={{ marginTop: 4 }}>
          {fl.forward.map((d) => (
            <li key={d.target}>
              <span className="mono">{d.target}</span>{' — '}
              {t('{n} pacchetti inoltrati', { n: d.sent })}
              {d.spoofed > 0 && ` (${t('{n} con il mittente originale', { n: d.spoofed })})`}
              {d.errors > 0 && (
                <span style={{ color: 'var(--warn)' }}>
                  {' · '}{t('{n} errori', { n: d.errors })}{d.last_error ? `: ${d.last_error}` : ''}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {keepSource && fl?.forward && fl.forward.length > 0 && !fl.forward_keep_source && (
        <p className="field__hint" style={{ color: 'var(--warn)' }}>
          {t('La sonda non può mantenere il mittente originale e invia dal proprio indirizzo: {motivo}', { motivo: fl.forward_keep_source_error ?? '' })}
        </p>
      )}

      {/* --- Sorgente 2: cattura diretta dal filo --- */}
      <h4 style={{ margin: '18px 0 8px', fontSize: 14 }}>
        {t('Cattura diretta dal filo')}
      </h4>
      <div className="row">
        <div className="field">
          <label htmlFor="fl-capture">{t('Interfaccia')}</label>
          <select id="fl-capture" value={iface} onChange={(e) => setIface(e.target.value)}>
            <option value="">{t('Nessuna')}</option>
            {options.map((o) => (
              <option key={o.name} value={o.name}>
                {o.name} ({o.detail})
              </option>
            ))}
          </select>
          <p className="field__hint">
            {t("L'interfaccia collegata alla porta mirror (SPAN) dello switch. Vede solo il traffico che arriva lì: senza un mirror configurato, vedrà quasi soltanto il traffico della sonda stessa.")}
          </p>
        </div>

        {/* Sempre visibile, anche senza interfaccia scelta: è una decisione che
            cambia la scala dei dati, e nasconderla finché non si configura
            qualcos'altro la farebbe scoprire solo dopo. Disabilitata, perché
            senza cattura non ha nulla su cui applicarsi. */}
        <div className="field" style={{ maxWidth: 230 }}>
          <label htmlFor="fl-sampling">{t('Campionamento della cattura')}</label>
          <select
            id="fl-sampling"
            value={sampling}
            disabled={!iface}
            onChange={(e) => setSampling(e.target.value)}
          >
            <option value="1">{t('Tutti i pacchetti')}</option>
            <option value="10">{t('1 su 10 (predefinito)')}</option>
            <option value="100">{t('1 su {n}', { n: 100 })}</option>
            <option value="1000">{t('1 su {n}', { n: 1000 })}</option>
          </select>
          <p className="field__hint">
            {iface ? (
              <>
                {t('I volumi vengono riportati in scala, quindi restano confrontabili — a differenza dei pacchetti che il kernel scarta, dove il fattore non si conosce. Su reti tranquille scendi a 1.')}
              </>
            ) : (
              <>{t("Si applica alla cattura diretta: scegli prima un'interfaccia.")}</>
            )}
          </p>
        </div>
      </div>

      {/* The capture interface itself. A mirror with jumbo frames into an
          interface at 1500 loses every longer frame in the NIC, before the
          kernel: the capture never sees them and does not count them. */}
      <div className="row">
        <div className="field" style={{ maxWidth: 230 }}>
          <label htmlFor="fl-mtu">{t("MTU dell'interfaccia")}</label>
          <select
            id="fl-mtu"
            value={mtuOther ? 'other' : mtu}
            disabled={!iface}
            onChange={(e) => {
              const v = e.target.value
              setMtuOther(v === 'other')
              if (v !== 'other') setMtu(v)
            }}
          >
            <option value="">{t('Non toccare')}</option>
            <option value="1500">1500</option>
            <option value="9000">{t('9000 (jumbo)')}</option>
            <option value="9216">9216</option>
            <option value="other">{t('Altro…')}</option>
          </select>
          {mtuOther && (
            <input
              id="fl-mtu-other"
              type="number"
              min={MTU_MIN}
              max={MTU_MAX}
              value={mtu}
              disabled={!iface}
              aria-label={t("MTU dell'interfaccia")}
              style={{ marginTop: 6 }}
              onChange={(e) => setMtu(e.target.value)}
            />
          )}
          <p className="field__hint">
            {mtuError ? (
              <strong>{mtuError}</strong>
            ) : iface ? (
              <>
                {t("Se nel mirror passano jumbo frame (iSCSI, NFS, vMotion, backup), a 1500 la scheda butta via ogni frame più lungo prima che la cattura lo veda, e nessun contatore della cattura lo conta. Su un'interfaccia d'ascolto senza indirizzo un MTU alto non costa nulla.")}
              </>
            ) : (
              <>{t("Si applica alla cattura diretta: scegli prima un'interfaccia.")}</>
            )}
          </p>
        </div>
        <div className="field">
          <div className="checkbox">
            <input
              id="fl-offloads"
              type="checkbox"
              checked={offloadsOff}
              disabled={!iface}
              onChange={(e) => setOffloadsOff(e.target.checked)}
            />
            <label htmlFor="fl-offloads">
              {t('Disattiva GRO/LRO')}
              <small>
                {t('La scheda (LRO) o il kernel (GRO) uniscono segmenti consecutivi in un pacchetto solo prima che la cattura lo veda: conteggi, dimensioni e tempi non sono più quelli del filo. Su un mirror conviene spegnerli.')}
              </small>
            </label>
          </div>
        </div>
      </div>
      {iface && (mtu || offloadsOff) && (
        <p className="field__hint" style={{ marginTop: -6 }}>
          {t('La sonda li applica prima di mettersi in ascolto, e di nuovo a ogni riavvio della macchina. Gira senza privilegi di rete: li applica per lei un piccolo servizio di sistema ({servizio}), che l’aggiornatore installa da sé. In Docker la sonda va installata con {opzione}.', { servizio: 'censo-agent-net', opzione: '--allow-nic-settings' })}
        </p>
      )}

      {/* Cattura di prova: pochi secondi di traffico grezzo, scaricabili in
          pcap. Diagnostica a sé, indipendente dalla raccolta continua qui
          sopra: serve a rispondere «ma su questa interfaccia passa davvero
          qualcosa?» senza dover configurare nulla. */}
      <CatturaDiProva probe={probe} options={options} />

      {!usable.length && (
        <div className="banner banner--warn">
          {t('Questa sonda non dichiara interfacce adatte alla cattura: servono una scheda attiva e non di loopback, di norma collegata alla porta mirror. Verifica che sia aggiornata e che la scheda sia su.')}
        </div>
      )}

      {/* --- Soglie, comuni a entrambe le sorgenti --- */}
      <h4 style={{ margin: '18px 0 8px', fontSize: 14 }}>
        {t('Soglie (valgono per entrambe le sorgenti)')}
      </h4>

      {/* Soglie: quanto deve essere grande una conversazione per valere il
          viaggio. Valgono per **entrambe** le sorgenti — flussi esportati e
          cattura diretta — perché il rumore che si vuole togliere è lo stesso. */}
      <div className="row">
        <div className="field" style={{ maxWidth: 220 }}>
          <label htmlFor="min-packets">{t('Pacchetti minimi')}</label>
          <input
            id="min-packets"
            type="number"
            min={0}
            value={minPackets}
            onChange={(e) => setMinPackets(e.target.value)}
          />
          <p className="field__hint">{t('0 = nessuna soglia.')}</p>
        </div>
        <div className="field" style={{ maxWidth: 220 }}>
          <label htmlFor="min-bytes">{t('Byte minimi')}</label>
          <input
            id="min-bytes"
            type="number"
            min={0}
            value={minBytes}
            onChange={(e) => setMinBytes(e.target.value)}
          />
          <p className="field__hint">{t('0 = nessuna soglia. 5120 = 5 KB.')}</p>
        </div>
      </div>
      <p className="field__hint" style={{ marginTop: -6 }}>
        {/* Il punto che si sbaglia leggendo: è un OR, non un AND. */}
        {t('Basta superarne una: «20 pacchetti o 5 KB» descrive due modi di essere interessante, non due condizioni insieme. In AND si butterebbero via proprio le conversazioni grosse e brevi — pochi pacchetti, molti byte, cioè un trasferimento. Con entrambe a zero non si scarta nulla.')}
      </p>

      <CaptureState
        capture={probe.capture}
        flows={probe.flows}
        wanted={iface}
        capabilityOn={probe.effective_capabilities.includes('netflow')}
        lastHeartbeat={probe.last_heartbeat_at}
      />

      <button onClick={save} disabled={busy || portError !== null || mtuError !== null}>
        {busy ? t('Salvataggio…') : t('Salva sorgenti')}
      </button>
    </Card>
  )
}

/**
 * Cattura di prova: pochi secondi di traffico grezzo su un'interfaccia, in pcap.
 *
 * È diagnostica, non raccolta: risponde a «su questa interfaccia passa davvero
 * qualcosa?» senza toccare la configurazione della raccolta continua. Avvia un
 * job `traffic_sample`, ne segue lo stato, e appena è pronto scarica il file —
 * apribile in Wireshark. Un job per volta: la cattura è breve e sequenziale.
 */
function CatturaDiProva({
  probe,
  options,
}: {
  probe: Detail
  options: { name: string; detail: string }[]
}) {
  const [iface, setIface] = useState(options[0]?.name ?? '')
  // Filtro porte facoltativo (testo, es. "2055,6343"): tiene solo i pacchetti su
  // quelle porte. Serve a rispondere «su questa porta arriva NetFlow?» senza
  // affogare il pcap nel resto del traffico.
  const [porte, setPorte] = useState('')
  // Only the devices' announcements: the sample for a new identity decoder.
  const [soloAnnunci, setSoloAnnunci] = useState(false)
  const [stato, setStato] = useState<'idle' | 'attesa' | 'scarico' | 'fatto' | 'errore'>('idle')
  const [error, setError] = useState<unknown>(null)
  const [jobId, setJobId] = useState<string | null>(null)

  // Le porte convenzionali degli esportatori di flussi, per il preset.
  const PORTE_FLUSSI = '2055,6343,9995,9996,4739'
  // Dal testo alla lista di numeri validi: si ignorano vuoti e refusi, così il
  // campo resta libero ma non manda al server porte senza senso.
  const porteValide = porte
    .split(/[\s,]+/)
    .map((p) => Number(p))
    .filter((p) => Number.isInteger(p) && p >= 1 && p <= 65535)

  // Segue il job finché non è in stato terminale, poi scarica il pcap.
  //
  // Le dipendenze sono **solo** `jobId`: `stato` NON deve starci. Cambiarlo a
  // «scarico» mentre il download è in corso ricreerebbe l'effetto, la sua
  // pulizia porrebbe `vivo=false`, e il download in volo verrebbe abortito
  // dalla guardia `if (!vivo)`. Era il motivo per cui «non scaricava»: la
  // cattura finiva, ma il file non arrivava mai.
  useEffect(() => {
    if (!jobId) return
    let vivo = true
    let timer: ReturnType<typeof setTimeout>
    async function giro() {
      try {
        const job = await api.job(jobId as string)
        if (!vivo) return
        if (job.status === 'succeeded' || job.status === 'partial') {
          setStato('scarico')
          const blob = await api.jobPcap(jobId as string)
          if (!vivo) return
          // Come per il certificato: l'ancora va nel DOM prima del click, e la
          // revoca dell'URL avviene dopo la rimozione — revocarlo troppo presto
          // annulla il download su alcuni browser.
          const url = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = url
          a.download = `cattura-${(iface || 'auto').replace(/[^\w.-]+/g, '_')}.pcap`
          document.body.appendChild(a)
          a.click()
          a.remove()
          URL.revokeObjectURL(url)
          setStato('fatto')
        } else if (job.status === 'failed') {
          setError(new Error(job.error_message || t('La cattura non è riuscita.')))
          setStato('errore')
        } else {
          timer = setTimeout(giro, 1500)
        }
      } catch (err) {
        if (!vivo) return
        setError(err)
        setStato('errore')
      }
    }
    giro()
    return () => {
      vivo = false
      clearTimeout(timer)
    }
  }, [jobId, iface])

  async function avvia() {
    setError(null)
    setStato('attesa')
    try {
      const job = await api.createJob({
        kind: 'traffic_sample',
        probe_id: probe.id,
        targets: [],
        capture_interface: iface || null,
        capture_seconds: soloAnnunci ? 90 : 10,
        ...(soloAnnunci ? { announce_only: true } : {}),
        ...(porteValide.length ? { capture_ports: porteValide } : {}),
      })
      setJobId(job.id)
    } catch (err) {
      setError(err)
      setStato('errore')
    }
  }

  const inCorso = stato === 'attesa' || stato === 'scarico'

  return (
    <div style={{ margin: '14px 0 4px' }}>
      <ErrorBanner error={error} />
      <div className="row" style={{ alignItems: 'flex-end' }}>
        <div className="field" style={{ maxWidth: 260 }}>
          <label htmlFor="cattura-iface">{t('Interfaccia da campionare')}</label>
          <select
            id="cattura-iface"
            value={iface}
            disabled={inCorso}
            onChange={(e) => setIface(e.target.value)}
          >
            <option value="">{t('Predefinita della sonda')}</option>
            {options.map((o) => (
              <option key={o.name} value={o.name}>
                {o.name} ({o.detail})
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ maxWidth: 200 }}>
          <label htmlFor="cattura-porte">{t('Porte (opzionale)')}</label>
          <input
            id="cattura-porte"
            type="text"
            inputMode="numeric"
            placeholder={t('es. 2055, 6343')}
            value={porte}
            disabled={inCorso}
            onChange={(e) => setPorte(e.target.value)}
          />
        </div>
        <div className="field" style={{ maxWidth: 260 }}>
          <button className="secondary" onClick={avvia} disabled={inCorso}>
            {stato === 'attesa'
              ? t('Cattura in corso…')
              : stato === 'scarico'
                ? t('Scarico…')
                : soloAnnunci ? t('Cattura degli annunci (90s)') : t('Cattura di prova (10s)')}
          </button>
        </div>
      </div>
      <div className="checkbox">
        <input id="cattura-annunci" type="checkbox" checked={soloAnnunci} disabled={inCorso}
               onChange={(e) => setSoloAnnunci(e.target.checked)} />
        <label htmlFor="cattura-annunci">
          {t('Solo gli annunci degli apparati')}
          <small>
            {t('Tiene solo ciò che gli apparati mandano a tutti (broadcast e multicast) per presentarsi in rete: nessuna conversazione fra due macchine, quindi nessun traffico clinico, e nessuna delle porte GE che portano il nome del paziente. Serve a scrivere il riconoscimento di un nuovo costruttore dai suoi annunci. Meglio farla con apparati non associati a un paziente (in laboratorio o in attesa), perché un costruttore che non conosciamo potrebbe mettere qualcosa di troppo anche negli annunci.')}
          </small>
        </label>
      </div>
      <p className="field__hint">
        {stato === 'fatto' ? (
          t('Cattura scaricata. Aprila in Wireshark. Puoi rilanciarla quando vuoi.')
        ) : (
          <>
            {t('Registra circa 10 secondi di traffico sull’interfaccia e scarica un file .pcap da aprire in Wireshark. Non modifica la raccolta continua.')}{' '}
            {t('Con le porte indicate tiene solo quei pacchetti — utile per vedere se su una porta arriva NetFlow/sFlow.')}{' '}
            <button type="button" className="link" disabled={inCorso} onClick={() => setPorte(PORTE_FLUSSI)}>
              {t('Usa le porte dei flussi')}
            </button>
          </>
        )}
      </p>
    </div>
  )
}

/**
 * Diagnosi flussi: un click, la sonda annusa la porta dei flussi per qualche
 * secondo e riferisce se e a chi arrivano — senza shell né Wireshark.
 *
 * Lancia un job `flow_diag` e ne mostra gli **eventi** appena finito: è lì che
 * la sonda scrive l'esito (configurazione, cosa passa sul filo, verdetto). Come
 * per la cattura di prova, il polling dipende **solo** da `jobId` — mettere lo
 * stato fra le dipendenze abortirebbe la fetch del rapporto a metà.
 */
function DiagnosiFlussi({ probe }: { probe: Detail }) {
  const [stato, setStato] = useState<'idle' | 'attesa' | 'fatto' | 'errore'>('idle')
  const [error, setError] = useState<unknown>(null)
  const [jobId, setJobId] = useState<string | null>(null)
  const [eventi, setEventi] = useState<JobEvent[]>([])

  useEffect(() => {
    if (!jobId) return
    let vivo = true
    let timer: ReturnType<typeof setTimeout>
    async function giro() {
      try {
        const job = await api.job(jobId as string)
        if (!vivo) return
        if (job.status === 'succeeded' || job.status === 'partial' || job.status === 'failed') {
          const rep = await api.jobReport(jobId as string)
          if (!vivo) return
          setEventi(rep.events.filter((e) => e.level !== 'debug'))
          setStato('fatto')
        } else {
          timer = setTimeout(giro, 1500)
        }
      } catch (err) {
        if (!vivo) return
        setError(err)
        setStato('errore')
      }
    }
    giro()
    return () => {
      vivo = false
      clearTimeout(timer)
    }
  }, [jobId])

  async function avvia() {
    setError(null)
    setEventi([])
    setStato('attesa')
    try {
      const job = await api.createJob({ kind: 'flow_diag', probe_id: probe.id, targets: [] })
      setJobId(job.id)
    } catch (err) {
      setError(err)
      setStato('errore')
    }
  }

  const colore = (livello: string) =>
    livello === 'error'
      ? 'var(--danger, #c62828)'
      : livello === 'warning'
        ? 'var(--warn)'
        : 'var(--text)'

  return (
    <div style={{ margin: '12px 0 4px' }}>
      <ErrorBanner error={error} />
      <button className="secondary" onClick={avvia} disabled={stato === 'attesa'}>
        {stato === 'attesa' ? t('Diagnosi in corso…') : t('Diagnosi flussi')}
      </button>
      <p className="field__hint">
        {t('Ascolta qualche secondo la porta dei flussi e riferisce se e a chi arrivano — la risposta a «sembra arrivi NetFlow ma la sonda non lo rileva».')}
      </p>
      {eventi.length > 0 && (
        <div className="card__inset" style={{ marginTop: 6 }}>
          {eventi.map((e) => (
            <div key={e.id} style={{ fontSize: 12, padding: '3px 0', color: colore(e.level) }}>
              <strong style={{ textTransform: 'uppercase', opacity: 0.7, marginRight: 6 }}>
                {e.stage}
              </strong>
              {e.message}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Che cosa sta effettivamente vedendo la cattura.
 *
 * Stati che sembrano uno solo: **non configurata**, **capability spenta**,
 * **in attesa del primo heartbeat**, **la sonda non riporta la cattura**,
 * **provata e fallita**, **in corso**. Mostrarli tutti come «attendi il
 * prossimo heartbeat» significa che chi ha sbagliato una capability o
 * dimenticato `CAP_NET_RAW` aspetta un evento che non arriverà mai, e va a
 * cercare il guasto sullo switch.
 *
 * Nessuna attesa qui è illimitata. Un messaggio che dice «attendi» e non scade
 * è indistinguibile da un guasto, e il tempo lo perde chi legge.
 *
 * Ogni ramo dice **quello che la sonda riporta**, e non un'ipotesi ricavata
 * dall'assenza. La differenza non è di stile: la versione precedente di questo
 * componente, non vedendo alcuna cattura in un heartbeat recente, concludeva
 * «non la sta eseguendo, aggiorna la sonda» — e sbagliava due volte. La sonda
 * rilegge la configurazione dei flussi **una volta al minuto** mentre
 * l'heartbeat parte più spesso, quindi una sonda che ha appena riferito può non
 * avere ancora saputo che deve catturare; e fra le cause nominava «un sistema
 * che non è Linux», che non può produrre quel sintomo — fuori da Linux la
 * cattura restituisce un errore esplicito, che finisce nel ramo dell'errore, non
 * nel silenzio. Ora la sonda riporta il proprio stato di configurazione e qui si
 * legge, invece di indovinarlo.
 */
function AscoltoPassivo({ probe }: { probe: Detail }) {
  const avvistati = usePolling(() => api.probeSightings(probe.id), 30000, [probe.id])
  const st = probe.listen
  // Acceso con la capability, oppure con la cattura dal filo configurata:
  // cattura e analisi sono una funzionalità sola (lato server decide la licenza).
  const conCattura = Boolean(((probe.labels ?? {}) as Record<string, unknown>).flow_capture)
  const acceso = probe.effective_capabilities.includes('passive_listen') || conCattura
  const righe = avvistati.data ?? []
  return (
    <Card title={t('Ascolto passivo')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('La sonda sente dalla propria scheda gli annunci dei dispositivi sul segmento (ARP, DHCP, mDNS, SSDP, NetBIOS, LLDP) e consegna un riassunto per MAC ogni due minuti. Qui vedi se ascolta e chi ha sentito: un dispositivo in tabella è la prova che intercetta.')}{' '}
        {t('Per sentire più VLAN porta alla sonda una porta trunk con tutte le VLAN consentite: legge il tag 802.1Q di ogni frame e segna la VLAN in tabella, senza sottointerfacce sulla macchina.')}{' '}
        {t('Con la cattura dal filo configurata l’ascolto è acceso da solo, senza spunta: la cattura produce le conversazioni (chi parla con chi, volumi), l’analisi l’identità dei dispositivi, sugli stessi pacchetti letti una volta sola («(cattura)» accanto al nome della scheda). La capability «Ascolto passivo» serve solo per ascoltare schede senza cattura (management, trunk).')}
      </p>
      {!acceso ? (
        <p className="field__hint">{t('L’ascolto è spento: configura la cattura dal filo (lo accende da sé) oppure attiva la capability «Ascolto passivo» fra le capability qui sopra.')}</p>
      ) : !st ? (
        <p className="field__hint">{t('La sonda non riporta ancora lo stato dell’ascolto: aspetta il prossimo heartbeat, o aggiornala se è una versione precedente.')}</p>
      ) : (
        <dl className="kv" style={{ marginBottom: 12 }}>
          <dt>{t('Stato')}</dt>
          <dd>
            {st.running ? <span className="badge badge--ok">{t('in ascolto')}</span> : <span className="badge badge--warn">{t('non in ascolto')}</span>}
            {st.error && <> <span className="text-muted">{st.error}</span></>}
          </dd>
          <dt>{t('Schede')}</dt>
          <dd className="mono">{st.interfaces.length ? st.interfaces.join(', ') : '—'}</dd>
          <dt>{t('Pacchetti utili sentiti')}</dt>
          <dd>{st.packets.toLocaleString(localeCorrente())}</dd>
          <dt>{t('Dispositivi consegnati')}</dt>
          <dd>
            {st.delivered_devices.toLocaleString(localeCorrente())}
            {st.last_delivery && <span className="text-muted"> · {t('ultima consegna {q}', { q: st.last_delivery.slice(0, 16).replace('T', ' ') })}</span>}
          </dd>
          <dt>{t('Utilizzo AI')}</dt>
          <dd>
            {st.ai_catalog ? (
              <>{t('catalogo {n} servizi', { n: st.ai_catalog })} · {t('{n} minuti d’uso riconosciuti', { n: (st.ai_recognized ?? 0).toLocaleString(localeCorrente()) })}</>
            ) : (
              <span className="text-muted">{t('catalogo AI non ricevuto (add-on non attivo o sonda da aggiornare)')}</span>
            )}
          </dd>
          {st.delivery && (
            <>
              <dt>{t('Consegna al Control Plane')}</dt>
              <dd>
                {st.delivery.error ? (
                  <span style={{ color: 'var(--warn)' }}>
                    {t('non riuscita dal {q}', { q: (st.delivery.error_at ?? '').slice(0, 16).replace('T', ' ') })}
                  </span>
                ) : st.delivery.last_delivery_at ? (
                  t('riuscita, l’ultima il {q}', { q: st.delivery.last_delivery_at.slice(0, 16).replace('T', ' ') })
                ) : (
                  <span className="text-muted">{t('ancora nessuna consegna')}</span>
                )}
                {st.delivery.pending > 0 && <span className="text-muted"> · {t('{n} in attesa', { n: st.delivery.pending.toLocaleString(localeCorrente()) })}</span>}
              </dd>
            </>
          )}
          {!!st.ai_catalog && st.ai_names && (
            <>
              <dt>{t('Nomi letti dal filo')}</dt>
              <dd>
                {t('TLS {a} · QUIC {b} · DNS {c}', {
                  a: st.ai_names.tls.toLocaleString(localeCorrente()),
                  b: st.ai_names.quic.toLocaleString(localeCorrente()),
                  c: st.ai_names.dns.toLocaleString(localeCorrente()),
                })}
                {(st.ai_ech ?? 0) > 0 && <span className="text-muted"> · {t('{n} con il nome cifrato (ECH)', { n: (st.ai_ech ?? 0).toLocaleString(localeCorrente()) })}</span>}
              </dd>
            </>
          )}
        </dl>
      )}
      {acceso && st?.running && !!st.ai_catalog && st.ai_names && st.ai_names.tls + st.ai_names.quic + st.ai_names.dns === 0 && (
        <p className="field__hint">{t('Il lettore AI non vede nessun nome sul filo: il mirror non porta il traffico dei client verso Internet né le loro domande DNS. Controlla che la porta mirror copi entrambe le direzioni del collegamento verso il firewall (o del core), non solo un segmento di server.')}</p>
      )}
      {acceso && st?.running && (st?.ai_external ?? 0) > 0 && (
        <p className="field__hint">{t('{n} nomi chiesti da indirizzi fuori dalle reti interne sono stati scartati. Se la rete usa indirizzi pubblici propri, o il mirror sta dopo il NAT, aggiungili alle reti interne in Impostazioni → Traffico da conservare.', { n: (st?.ai_external ?? 0).toLocaleString(localeCorrente()) })}</p>
      )}
      {acceso && st?.delivery?.error && (
        <div className="banner banner--warn">
          <strong>{t('Ciò che la sonda sente non arriva al Control Plane.')}</strong>{' '}
          <span className="mono">{st.delivery.error}</span>
          <div style={{ marginTop: 6 }}>
            {t('Un lotto rifiutato si scarta e non blocca più gli altri; un Control Plane lento o irraggiungibile fa aspettare il resto al giro dopo.')}
          </div>
        </div>
      )}
      {acceso && st?.delivery && st.delivery.discarded + st.delivery.refused > 0 && (
        <p className="field__hint">
          {t('Avvistamenti persi dall’avvio dell’ascolto: {a} oltre la memoria della sonda, {b} rifiutati dal Control Plane.', {
            a: st.delivery.discarded.toLocaleString(localeCorrente()),
            b: st.delivery.refused.toLocaleString(localeCorrente()),
          })}
        </p>
      )}
      {acceso && st?.running && st.packets === 0 && (
        <p className="field__hint">{t('Ascolta ma non sente nulla: sulla scheda della sonda non passano annunci. In una VM controlla che la porta del vSwitch sia sullo stesso segmento dei dispositivi; in container serve la rete dell’host.')}</p>
      )}
      <ErrorBanner error={avvistati.error} />
      {righe.length > 0 && (
        <DataTable rows={righe} columns={colonneAvvistati()} rowKey={(r) => r.mac} conteggio={{ nome: t('dispositivi') }} />
      )}
    </Card>
  )
}

function CaptureState({
  capture,
  flows,
  wanted,
  capabilityOn,
  lastHeartbeat,
}: {
  capture: Detail['capture']
  flows: Detail['flows']
  /** L'interfaccia richiesta dalla console, per confrontarla con quella applicata. */
  wanted: string
  capabilityOn: boolean
  lastHeartbeat: string | null
}) {
  if (!wanted) return null

  // La cattura vive dentro la capability `netflow`: senza, la sonda non riceve
  // nemmeno la configurazione e non partirà mai. È la causa più facile da
  // creare dalla console e la più difficile da indovinare.
  if (!capabilityOn) {
    return (
      <div className="banner banner--warn">
        {t('La capability {nome} è spenta su questa sonda: la cattura non partirà, per quanto si aspetti. Riattivala fra le capability qui sopra.', { nome: 'netflow' })}
      </div>
    )
  }

  if (!capture) {
    const heardFrom = lastHeartbeat ? Date.now() - new Date(lastHeartbeat).getTime() : null
    const silent = heardFrom === null || heardFrom > 3 * 60 * 1000

    // Finché la sonda tace non si può dire nulla sulla cattura: qualunque altra
    // frase sarebbe un'ipotesi su una sonda che non risponde. Questo ramo viene
    // prima degli altri perché anche i dati riportati, se sono vecchi, dicono al
    // massimo com'era la situazione allora.
    if (silent) {
      return (
        <div className="banner banner--warn">
          {lastHeartbeat
            ? t('La sonda non si fa sentire da {tempo}.', { tempo: humanAge(Math.round(heardFrom! / 1000)) })
            : t('Questa sonda non ha ancora inviato un heartbeat.')}{' '}
          {t('Finché tace non riceve nemmeno la configurazione, quindi la cattura non può partire e quello che si vede qui non è aggiornato. Verifica che il processo sia in esecuzione e che raggiunga il Control Plane.')}
        </div>
      )
    }

    // La sonda riferisce ma non dice **niente** della raccolta di flussi. È
    // l'unico caso in cui «aggiorna la sonda» è la diagnosi giusta: le versioni
    // che sanno catturare riportano sempre questa sezione, anche per dire che non
    // stanno catturando.
    if (!flows) {
      return (
        <div className="banner banner--warn">
          {t('La sonda ha riferito {tempo} fa ma non riporta nulla sulla raccolta di flussi: è una versione precedente alla cattura diretta. Aggiornala — le versioni che sanno catturare riportano questo stato anche quando non stanno catturando.', { tempo: humanAge(Math.round(heardFrom! / 1000)) })}
        </div>
      )
    }

    // La sonda non riesce a leggere la configurazione, e sa perché. Prima questo
    // errore restava in un log a livello di dettaglio dentro la rete del cliente,
    // e da qui era indistinguibile dall'attesa.
    if (flows.error) {
      return (
        <div className="banner banner--error">
          <strong>{t('La sonda non riesce a leggere la configurazione dei flussi.')}</strong>{' '}
          <span className="mono">{flows.error}</span>
          <div style={{ marginTop: 6 }}>
            {t('Segnalato {tempo} fa. La cattura non partirà finché non riesce a leggerla: la configurazione dell’ascolto vive nel cloud, non sulla macchina.', { tempo: humanAge(flows.error_age_seconds ?? 0) })}
          </div>
        </div>
      )
    }

    // Non ha ancora completato la prima lettura. Dura pochi secondi dall'avvio: è
    // una finestra reale, ma stretta, e va detta come tale invece di lasciarla
    // sembrare uno stato stabile.
    if (!flows.config_seen) {
      return (
        <div className="banner banner--info">
          {t("La sonda è in contatto ma non ha ancora letto la configurazione dei flussi: succede nei primi secondi dopo l'avvio. Se resta così, l'errore comparirà qui al prossimo tentativo.")}
        </div>
      )
    }

    const age = flows.config_age_seconds ?? 0

    // Ha una configurazione, ma non ancora quella salvata qui. La rilettura è
    // ogni minuto: è questo il numero che governa l'attesa, non l'heartbeat —
    // confondere i due era la ragione per cui il banner precedente dichiarava
    // guasta una sonda che stava semplicemente per aggiornarsi.
    if (flows.capture_interface !== wanted) {
      const overdue = age > 90
      return (
        <div className={`banner banner--${overdue ? 'warn' : 'info'}`}>
          {flows.capture_interface
            ? t('La sonda ha una configurazione di {tempo} fa che chiede la cattura su {ha}, non su {voluta}.', { tempo: humanAge(age), ha: flows.capture_interface, voluta: wanted })
            : t('La sonda ha una configurazione di {tempo} fa che non chiede alcuna cattura.', { tempo: humanAge(age) })}{' '}
          {overdue
            ? t('Avrebbe dovuto rileggerla entro un minuto: se non cambia, la sonda non sta più riuscendo a interrogare il Control Plane.')
            : t('La rilegge ogni minuto: la cattura parte al prossimo giro.')}
        </div>
      )
    }

    // La raccolta è disabilitata nella configurazione che la sonda ha davvero
    // ricevuto. Diverso dalla capability spenta in console — che è già stata
    // controllata sopra — perché qui il cloud e la sonda sono in disaccordo.
    if (!flows.enabled) {
      return (
        <div className="banner banner--warn">
          {t('La configurazione ricevuta dalla sonda {tempo} fa non abilita la raccolta di flussi, pur essendo la capability attiva in console. Di norma significa che la licenza del tenant non comprende i flussi: controlla gli entitlement.', { tempo: humanAge(age) })}
        </div>
      )
    }

    // Configurazione applicata, interfaccia giusta, raccolta abilitata, e
    // nessuna cattura. Non ha una causa nota: si dicono i fatti e non si
    // inventa una spiegazione.
    return (
      <div className="banner banner--error">
        {t('La sonda ha applicato la cattura su {interfaccia} ({tempo} fa) ma non riporta alcuno stato di cattura. È una combinazione che non dovrebbe verificarsi: raccogli i log della sonda («cattura diretta») prima di riavviarla, altrimenti la causa si perde.', { interfaccia: wanted, tempo: humanAge(age) })}
      </div>
    )
  }

  // La sonda ha provato e non ce l'ha fatta, e sa perché. Il motivo arriva
  // dall'heartbeat: senza, questo sarebbe indistinguibile dall'attesa.
  if (!capture.running && capture.error) {
    return (
      <div className="banner banner--error">
        <strong>{t('Cattura non avviata.')}</strong> {capture.error}
        <div style={{ marginTop: 6 }}>
          {t('Se manca {cap}: in Docker servono {opzione} e la rete dell’host. La ricezione dei flussi esportati non dipende da questo e continua a funzionare.', { cap: 'CAP_NET_RAW', opzione: '--cap-add=NET_RAW' })}
        </div>
      </div>
    )
  }

  const nic = capture.nic ?? null
  const since = nic?.rx_errors_since_start ?? {}
  const tooLong = since.length_errors ?? 0
  const ringFull = (since.over_errors ?? 0) + (since.missed_errors ?? 0)

  return (
    <>
      <dl className="kv">
        <dt>{t('Cattura su')}</dt>
        <dd className="mono">
          {capture.interface}
          {capture.sampling > 1 && ` · ${t('1 pacchetto su {n}', { n: capture.sampling })}`}
          {!capture.running && (
            <span className="text-muted"> · {t('fermata')}</span>
          )}
        </dd>
        <dt>{t('Pacchetti osservati')}</dt>
        <dd>
          {capture.packets.toLocaleString(localeCorrente())}
          {capture.ignored > 0 && (
            <span className="text-muted">
              {' '}
              · {t('{n} non IP (ARP, STP, LLDP)', { n: capture.ignored.toLocaleString(localeCorrente()) })}
            </span>
          )}
        </dd>
        <dt>{t('Flussi prodotti')}</dt>
        <dd>{capture.flows.toLocaleString(localeCorrente())}</dd>
        {capture.readers != null && (
          <>
            <dt>{t('Lettura')}</dt>
            <dd>
              {capture.ring
                ? t('anello del kernel · {n} lettori in parallelo', { n: capture.readers })
                : t('classica, un pacchetto per chiamata: il kernel non ha concesso l’anello')}
            </dd>
          </>
        )}
        {nic && nic.mtu > 0 && (
          <>
            <dt>{t('Interfaccia')}</dt>
            <dd>
              {t('MTU {n}', { n: nic.mtu })}
              {nic.requested_mtu > 0 && nic.requested_mtu !== nic.mtu && (
                <span style={{ color: 'var(--warn)' }}> ({t('richiesta {n}', { n: nic.requested_mtu })})</span>
              )}
              {nic.gro != null && <> · GRO {nic.gro ? t('acceso') : t('spento')}</>}
              {nic.lro != null && <> · LRO {nic.lro ? t('acceso') : t('spento')}</>}
            </dd>
          </>
        )}
      </dl>

      {nic && nic.problems.length > 0 && (
        <div className="banner banner--warn">
          <strong>{t('Interfaccia di cattura non preparata del tutto.')}</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {nic.problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}
      {tooLong > 0 && nic && (
        // Frames refused by the NIC before the kernel: neither the capture nor
        // the kernel's drop counter sees them — only the interface does.
        <div className="banner banner--warn">
          {t('Da quando la cattura è partita la scheda ha rifiutato {n} frame più lunghi del suo MTU ({mtu}): di norma jumbo frame nel mirror (iSCSI, NFS, vMotion, backup). Non arrivano alla cattura e nessun altro contatore li conta. Imposta qui sopra l’MTU dell’interfaccia a 9000 o 9216.', { n: tooLong.toLocaleString(localeCorrente()), mtu: nic.mtu })}
        </div>
      )}
      {ringFull > 0 && (
        <div className="banner banner--warn">
          {t('Da quando la cattura è partita la scheda ha perso {n} frame perché il suo anello di ricezione era pieno, prima del kernel. Se crescono, ingrandisci l’anello della scheda (ethtool -G {interfaccia} rx 4096) o copia meno traffico nello SPAN.', { n: ringFull.toLocaleString(localeCorrente()), interfaccia: capture.interface })}
        </div>
      )}

      {capture.dropped > 0 && (
        // È l'unico modo di sapere che i volumi sono incompleti: senza questo
        // avviso una cattura satura è indistinguibile da una rete tranquilla.
        <div className="banner banner--warn">
          {t('Il kernel ha scartato {n} pacchetti: la sonda non sta al passo con il traffico e i volumi riportati sono incompleti. Le perdite del kernel, a differenza del campionamento, non si possono correggere: il fattore non si conosce. Il campionamento non le riduce, perché la sonda legge comunque ogni pacchetto: copia meno traffico nello SPAN (solo le VLAN dei dispositivi, non storage e backup) oppure usa una sonda con più core.', { n: capture.dropped.toLocaleString(localeCorrente()) })}
        </div>
      )}
      {capture.packets > 0 && capture.flows === 0 && (
        <div className="banner banner--warn">
          {t('Nessun flusso da {n} pacchetti: sull’interfaccia arriva traffico ma non è IP. Verifica che la porta mirror stia replicando le VLAN giuste.', { n: capture.packets.toLocaleString(localeCorrente()) })}
        </div>
      )}
    </>
  )
}

/**
 * I comandi utili per amministrare una sonda dalla macchina che la ospita.
 *
 * Sta in console, accanto alla sonda, e non in un manuale a parte: chi ha un
 * problema con una sonda apre la sua scheda, e la domanda — «come vedo i log?
 * come la aggiorno?» — nasce qui. La sonda è un servizio systemd installato
 * dall'installer (`censo-agent`), non un container: i comandi sono quelli, da
 * eseguire sulla macchina che la ospita, come root o con sudo.
 */
function Manutenzione({
  version,
  servizio,
  comandoAggiornatore,
  appliance,
}: {
  version: string | null
  servizio: string
  comandoAggiornatore?: string
  appliance: boolean
}) {
  // Only when the Control Plane does not send `updater_command` (older one):
  // the same reading of the unit as the updater (ADABS_ for the probes
  // installed back then). sudo on each line, not `sudo -i`: pasted lines after
  // it would be read as the password.
  const bootstrap = [
    `U=/etc/systemd/system/${servizio}.service`,
    "CP=$(sudo sed -n -E 's/^Environment=(CENSO|ADABS)_CONTROL_PLANE_URL=//p' $U | tail -1)",
    "CA=$(sudo sed -n -E 's/^Environment=(CENSO|ADABS)_SERVER_CA_FILE=//p' $U | tail -1)",
    `sudo curl -fsS --cert /var/lib/${servizio}/agent.cert.pem --key /var/lib/${servizio}/agent.key.pem \\`,
    `  \${CA:+--cacert "$CA"} "$CP/api/v1/appliance/updater" -o /usr/local/sbin/${servizio}-update`,
    `sudo chmod 755 /usr/local/sbin/${servizio}-update`,
    `sudo /usr/local/sbin/${servizio}-update`,
  ].join('\n')

  return (
    <div className="manutenzione">
      <p className="field__hint">
        {t(
          'La sonda è un servizio systemd che si chiama {servizio}. I comandi vanno eseguiti sulla macchina che la ospita, come root o con sudo.',
          { servizio },
        )}
      </p>

      <h4>{t('Vedere i log')}</h4>
      <pre>
        <code>{`journalctl -u ${servizio} -f`}</code>
      </pre>
      <p className="field__hint">
        {t('In coda, solo le ultime righe (utile dopo un errore):')}
      </p>
      <pre>
        <code>{`journalctl -u ${servizio} -n 200 --no-pager`}</code>
      </pre>

      <h4>{t('Stato e riavvio')}</h4>
      <pre>
        <code>{`sudo systemctl status ${servizio}`}</code>
      </pre>
      <p className="field__hint">
        {t('Un riavvio non perde l’identità né i dati (stanno in /var/lib/{servizio}):', { servizio })}
      </p>
      <pre>
        <code>{`sudo systemctl restart ${servizio}`}</code>
      </pre>

      <h4>{t('Forzare l’aggiornamento')}</h4>
      <p className="field__hint">
        {t(
          'L’installer lascia sulla macchina lo script di aggiornamento e un timer che lo esegue da sé. Per forzarlo adesso, scarica il binario nuovo dal Control Plane (con l’identità della sonda, nessun token) e riavvia il servizio:',
        )}
      </p>
      <pre>
        <code>{`sudo /usr/local/sbin/${servizio}-update`}</code>
      </pre>
      <p className="field__hint">
        {t(
          'Se è già aggiornata non fa niente e lo dice; se il servizio non risale, rimette il binario precedente e riavvia. L’identità non si tocca: la sonda non si riarruola. Dopo, qui la «Versione della sonda» dice se l’aggiornamento è arrivato.',
        )}
        {version ? ` ${t('Versione attuale:')} ` : ''}
        {version && <code>{version}</code>}
      </p>

      <h4>{t('Aggiornare l’aggiornatore')}</h4>
      <p className="field__hint">
        {t(
          'Da ottobre 2026 l’aggiornatore si aggiorna da sé, una volta l’ora. Una sonda installata prima ha la copia vecchia, che non lo sa fare: va sostituita una volta con quella del Control Plane. Il comando la scarica e la esegue: installa timer e servizi mancanti e aggiorna la sonda se serve. Non riarruola la sonda: usa il suo certificato, nessun token.',
        )}
      </p>
      {appliance && (
        <div className="banner banner--info">
          {t(
            'Questa sonda è una Censo Appliance, senza shell: dal menu dell’appliance, con l’utente censoadmin, scegli «CENSO probe» → «Enroll / reinstall the probe» e incolla il comando qui sotto. Il menu ne prende solo l’indirizzo e l’impronta del certificato e lo esegue da root.',
          )}
        </div>
      )}
      <pre>
        <code>{comandoAggiornatore ?? bootstrap}</code>
      </pre>

      <h4>{t('Interfaccia di cattura (MTU, GRO/LRO)')}</h4>
      <p className="field__hint">
        {t(
          'Le impostazioni scelte in «Sorgenti dei flussi» le applica un servizio di sistema a parte, perché la sonda gira senza privilegi di rete: parte quando la sonda le cambia e a ogni avvio. Il suo stato e il suo esito:',
        )}
      </p>
      <pre>
        <code>{`sudo systemctl status ${servizio}-net.path ${servizio}-net.service\ncat /var/lib/${servizio}/capture-nic.result.json`}</code>
      </pre>

      <h4>{t('Se non torna online')}</h4>
      <p className="field__hint">
        {t(
          'Guarda i log (journalctl) per l’errore di arruolamento o di certificato; verifica che la macchina raggiunga il canale delle sonde (porta 443) e che l’ora di sistema sia corretta — un orologio sfasato fa fallire il TLS con un errore che parla di certificato. Il servizio è /etc/systemd/system/{servizio}.service, l’identità e lo stato stanno in /var/lib/{servizio}.',
          { servizio },
        )}
      </p>
    </div>
  )
}

function HostFacts({ probe }: { probe: Detail }) {
  const host = probe.host
  const runtime = probe.runtime as Record<string, unknown>
  const hasTelemetry = Object.keys(host).length > 0

  return (
    <dl className="kv">
      {hasTelemetry ? (
        <>
          <dt>{t('Sistema')}</dt>
          <dd>
            {host.os ?? '—'} {host.arch && <span className="mono">({host.arch})</span>}
            {host.kernel && <> · {t('kernel {v}', { v: host.kernel })}</>}
          </dd>
          {host.cpu_model && (
            <>
              <dt>{t('Processore')}</dt>
              <dd>{host.cpu_model}</dd>
            </>
          )}
          <dt>{t('Risorse')}</dt>
          <dd>
            {t('{n} core', { n: host.cpus ?? '—' })}
            {host.memory_mb ? ` · ${t('{v} di RAM', { v: formatMB(host.memory_mb) })}` : ''}
          </dd>
          {typeof host.cpu_percent === 'number' && (
            <>
              <dt>{t('Uso CPU')}</dt>
              <dd><Barra percento={Math.round(host.cpu_percent)} /></dd>
            </>
          )}
          {typeof host.memory_used_mb === 'number' && host.memory_mb ? (
            <>
              <dt>{t('Uso memoria')}</dt>
              <dd>
                <Barra percento={Math.round((host.memory_used_mb * 100) / host.memory_mb)} />
                <span className="text-muted"> {formatMB(host.memory_used_mb)} / {formatMB(host.memory_mb)}</span>
              </dd>
            </>
          ) : null}
          {(host.disks ?? []).length > 0 && (
            <>
              <dt>{t('Uso disco')}</dt>
              <dd>
                {(host.disks ?? []).map((d) => (
                  <div key={d.mount} style={{ marginBottom: 4 }}>
                    <Barra percento={d.percent} />
                    <span className="text-muted"> {d.mount} — {formatMB(d.used_mb)} / {formatMB(d.total_mb)}</span>
                  </div>
                ))}
              </dd>
            </>
          )}
          {typeof host.uptime_seconds === 'number' && (
            <>
              <dt>{t('In esecuzione da')}</dt>
              <dd>{formatDuration(host.uptime_seconds)}</dd>
            </>
          )}
        </>
      ) : (
        <>
          <dt>{t('Telemetria')}</dt>
          <dd className="text-muted">
            {t('Non riportata: questa sonda è precedente alla raccolta dei dati sulla macchina. Aggiornala per vedere host e interfacce.')}
          </dd>
        </>
      )}

      <dt>{t('Versione della sonda')}</dt>
      <dd className="mono">{probe.version ?? '—'}</dd>
      {typeof runtime.memory_mb === 'number' && (
        <>
          <dt>{t('Memoria in uso dal processo')}</dt>
          {/* A riposo la sonda sta sotto il megabyte: scrivere "0 MB" sembra un
              dato mancante, mentre il punto è proprio che non consuma nulla. */}
          <dd>{runtime.memory_mb === 0 ? t('meno di 1 MB') : `${runtime.memory_mb} MB`}</dd>
        </>
      )}
      <dt>{t('Ultimo heartbeat')}</dt>
      <dd title={probe.last_heartbeat_at ?? ''}>{timeAgo(probe.last_heartbeat_at)}</dd>
      <dt>{t('Arruolata il')}</dt>
      <dd>{formatDateTime(probe.created_at)}</dd>
      <dt>{t('Certificato client')}</dt>
      <dd className="mono" style={{ wordBreak: 'break-all' }}>
        {probe.cert_fingerprint ?? '—'}
      </dd>
    </dl>
  )
}

function Interfaces({ interfaces }: { interfaces: ProbeInterface[] }) {
  if (!interfaces.length) {
    return (
      <EmptyState
        title={t('Nessuna interfaccia riportata')}
        hint={t('Le sonde precedenti alla raccolta della telemetria non le inviano.')}
      />
    )
  }

  return (
    <DataTable
      rows={interfaces}
      columns={colonneInterfacce()}
      rowKey={(i, n) => i.name ?? String(n)}
      conteggio={{ nome: t('interfacce') }}
    />
  )
}

/** Dims every cell of a row (stale sightings, interfaces that are down). */
function attenuate<T>(colonne: Column<T>[], spenta: (row: T) => boolean, opacita: number): Column<T>[] {
  return colonne.map((col) => ({
    ...col,
    render: (row: T) => {
      const cella = col.render ? col.render(row) : (col.value?.(row) ?? '—')
      return spenta(row) ? <div style={{ opacity: opacita }}>{cella}</div> : cella
    },
  }))
}

/** The columns of «Ultime discovery». */
function colonneJob(): Column<ProbeJobBrief>[] {
  return [
    { key: 'kind', header: t('Tipo'), mono: true },
    { key: 'status', header: t('Stato'), render: (job) => <StatusBadge status={job.status} /> },
    {
      key: 'started_at',
      header: t('Avviato'),
      type: 'date',
      value: (job) => job.started_at ?? job.created_at,
      render: (job) => <span title={job.created_at}>{timeAgo(job.started_at ?? job.created_at)}</span>,
    },
    {
      key: 'completed_at',
      header: t('Concluso'),
      type: 'date',
      render: (job) => (job.completed_at ? formatDateTime(job.completed_at) : '—'),
    },
    {
      key: 'esito',
      header: t('Esito'),
      value: (job) => {
        if (job.error_message) return job.error_message
        const cis = job.stats?.accepted_cis ?? job.stats?.cis
        const errori = job.event_counts?.error ?? 0
        const avvisi = job.event_counts?.warning ?? 0
        return [
          cis !== undefined ? `${cis} CI` : '',
          errori > 0 ? (errori === 1 ? t('1 errore') : t('{n} errori', { n: errori })) : '',
          avvisi > 0 ? (avvisi === 1 ? t('1 avviso') : t('{n} avvisi', { n: avvisi })) : '',
        ].filter(Boolean).join(' ')
      },
      render: (job) =>
        job.error_message ? (
          <span style={{ color: 'var(--error)' }}>{job.error_message}</span>
        ) : (
          <JobCounts stats={job.stats} counts={job.event_counts} />
        ),
    },
  ]
}

/** The columns of the passive-listening table; stale devices are dimmed. */
function colonneAvvistati(): Column<ProbeSighting>[] {
  const meno = <span className="text-muted">—</span>
  return attenuate<ProbeSighting>(
    [
      { key: 'mac', header: 'MAC', mono: true },
      { key: 'vendor', header: t('Costruttore'), render: (r) => r.vendor ?? meno },
      { key: 'ips', header: 'IP', mono: true, value: (r) => r.ips.join(', '), render: (r) => r.ips.join(', ') || '—' },
      { key: 'vlan', header: 'VLAN', mono: true, value: (r) => r.vlan, render: (r) => r.vlan ?? meno },
      { key: 'hostname', header: t('Nome') },
      {
        key: 'tipo',
        header: t('Tipo'),
        value: (r) => [r.model, r.kind].filter(Boolean).join(' · '),
        render: (r) => [r.model, r.kind].filter(Boolean).join(' · ') || meno,
      },
      {
        key: 'sources',
        header: t('Fonti'),
        value: (r) => r.sources.join(', '),
        render: (r) => <small className="mono">{r.sources.join(', ')}</small>,
      },
      {
        key: 'last_seen',
        header: t('Ultima vista'),
        type: 'date',
        mono: true,
        render: (r) => (r.last_seen ? r.last_seen.slice(0, 16).replace('T', ' ') : '—'),
      },
    ],
    (r) => r.stale,
    0.6,
  )
}

/** The columns of the probe's network interfaces; the ones down are dimmed. */
function colonneInterfacce(): Column<ProbeInterface>[] {
  return attenuate<ProbeInterface>(
    [
      {
        key: 'name',
        header: t('Interfaccia'),
        value: (i) => [i.name, i.up ? t('attiva') : t('spenta'), i.loopback ? 'loopback' : ''].filter(Boolean).join(' '),
        render: (i) => (
          <>
            <span className="mono">{i.name ?? '—'}</span>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {i.up ? t('attiva') : t('spenta')}
              {i.loopback && ' · loopback'}
            </div>
          </>
        ),
      },
      { key: 'mac', header: 'MAC', mono: true },
      {
        key: 'addresses',
        header: t('Indirizzi'),
        mono: true,
        value: (i) => i.addresses.join(' '),
        render: (i) =>
          i.addresses.length ? i.addresses.map((a) => <div key={a}>{a}</div>) : <span className="text-muted">—</span>,
      },
      { key: 'mtu', header: 'MTU', value: (i) => i.mtu },
      {
        key: 'rx',
        header: t('Ricevuti'),
        value: (i) => i.rx_bytes,
        render: (i) => <Counter bytes={i.rx_bytes} packets={i.rx_packets} errors={i.rx_errors} />,
      },
      {
        key: 'tx',
        header: t('Trasmessi'),
        value: (i) => i.tx_bytes,
        render: (i) => <Counter bytes={i.tx_bytes} packets={i.tx_packets} errors={i.tx_errors} />,
      },
      {
        key: 'traffico',
        header: t('Traffico'),
        value: (i) => (i.rx_bps === null && i.tx_bps === null ? null : (i.rx_bps ?? 0) + (i.tx_bps ?? 0)),
        render: (i) => <Throughput rx={i.rx_bps} tx={i.tx_bps} />,
      },
    ],
    (i) => !i.up,
    0.55,
  )
}

/**
 * Un contatore cumulativo.
 *
 * `null` significa **non misurato**, non zero: mostrarne uno affermerebbe
 * l'assenza di traffico su una piattaforma dove semplicemente non lo leggiamo.
 */
function Counter({
  bytes,
  packets,
  errors,
}: {
  bytes: number | null
  packets: number | null
  errors: number | null
}) {
  if (bytes === null) return <span className="text-muted">{t('non misurato')}</span>
  return (
    <>
      {formatBytes(bytes)}
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
        {packets !== null && t('{n} pacchetti', { n: packets.toLocaleString(localeCorrente()) })}
        {errors ? <span style={{ color: 'var(--error)' }}> · {t('{n} errori', { n: errors })}</span> : null}
      </div>
    </>
  )
}

function Throughput({ rx, tx }: { rx: number | null; tx: number | null }) {
  if (rx === null && tx === null) return <span className="text-muted">—</span>
  return (
    <span className="mono" style={{ fontSize: 12 }}>
      ↓ {formatBytes(rx ?? 0)}/s
      <br />↑ {formatBytes(tx ?? 0)}/s
    </span>
  )
}

function JobCounts({
  stats,
  counts,
}: {
  stats?: Record<string, number>
  counts?: Record<string, number>
}) {
  const cis = stats?.accepted_cis ?? stats?.cis
  const errors = counts?.error ?? 0
  const warnings = counts?.warning ?? 0
  return (
    <>
      {cis !== undefined && <span className="chip">{cis} CI</span>}
      {errors > 0 && (
        <span className="chip" style={{ color: 'var(--error)' }}>
          {errors === 1 ? t('1 errore') : t('{n} errori', { n: errors })}
        </span>
      )}
      {warnings > 0 && (
        <span className="chip" style={{ color: 'var(--warn)' }}>
          {warnings === 1 ? t('1 avviso') : t('{n} avvisi', { n: warnings })}
        </span>
      )}
      {cis === undefined && !errors && !warnings && <span className="text-muted">—</span>}
    </>
  )
}

/** Byte in unità leggibili, in base 1024 come i sistemi operativi. */
function formatBytes(value: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  let n = value
  let unit = 0
  while (n >= 1024 && unit < units.length - 1) {
    n /= 1024
    unit += 1
  }
  return `${n < 10 && unit > 0 ? n.toFixed(1) : Math.round(n)} ${units[unit]}`
}

function formatMB(mb: number): string {
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`
}

/** Una barretta d'uso in percentuale: verde fin verso i tre quarti, ambra, poi
 *  rossa — l'utilizzo di CPU, memoria e disco della sonda a colpo d'occhio. */
function Barra({ percento }: { percento: number }) {
  const p = Math.max(0, Math.min(100, Math.round(percento)))
  const colore = p >= 90 ? 'var(--error)' : p >= 75 ? 'var(--warn)' : 'var(--ok)'
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ display: 'inline-block', width: 120, height: 8, background: 'var(--muted-bg)', borderRadius: 4, overflow: 'hidden', verticalAlign: 'middle' }}>
        <span style={{ display: 'block', width: `${p}%`, height: '100%', background: colore }} />
      </span>
      <span className="mono">{p}%</span>
    </span>
  )
}

/**
 * Un'età in forma leggibile, per le frasi che dicono «da quanto».
 *
 * Distinta da `formatDuration`, che descrive un uptime: qui i secondi contano,
 * perché la differenza fra «5 s fa» e «4 min fa» è la differenza fra una sonda
 * che sta per aggiornarsi e una che non ci riesce. Arrotondare a minuti
 * cancellerebbe esattamente l'informazione che serve.
 */
function humanAge(seconds: number): string {
  if (seconds < 60) return `${Math.max(0, Math.round(seconds))} s`
  const m = Math.floor(seconds / 60)
  if (m < 60) return `${m} min`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h} h`
  return t('{n} g', { n: Math.floor(h / 24) })
}

function formatDuration(seconds: number): string {
  const d = Math.floor(seconds / 86400)
  const h = Math.floor((seconds % 86400) / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (d > 0) return t('{d} g {h} h', { d, h })
  if (h > 0) return `${h} h ${m} min`
  // Una sonda appena riavviata è in esecuzione da meno di un minuto: "0 min"
  // si legge come un dato mancante invece che come "da pochissimo".
  if (m === 0) return `${seconds} s`
  return `${m} min`
}
